import type { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import Header from '@/components/header';
import Loader from '@/components/loader';
import { Sidebar } from '@/components/sidebar';
import { ThemeProvider } from '@/components/theme-provider';
import { Toaster } from '@/components/ui/sonner';
import type { trpc } from '@/utils/trpc';
import '../index.css';

export interface RouterAppContext {
  trpc: typeof trpc;
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
  component: RootComponent,
  head: () => ({
    meta: [
      {
        title: 'control-money',
      },
      {
        name: 'description',
        content: 'control-money is a web application',
      },
    ],
    links: [
      {
        rel: 'icon',
        href: '/favicon.ico',
      },
    ],
  }),
});

function RootComponent() {
  const isFetching = useRouterState({
    select: (s) => s.isLoading,
  });

  const currentPath = useRouterState({
    select: (s) => s.location.pathname,
  });

  // Hide navigation for auth pages
  const isAuthPage = currentPath === '/login' || currentPath === '/signup';

  return (
    <>
      <HeadContent />
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        disableTransitionOnChange
        storageKey="vite-ui-theme"
      >
        {isAuthPage ? (
          // Auth pages: no navigation, full screen
          <div className="h-svh">{isFetching ? <Loader /> : <Outlet />}</div>
        ) : (
          // Regular pages: with navigation
          <div className="grid h-svh grid-rows-[auto_1fr]">
            <Header />
            <div className="flex h-full overflow-hidden">
              {/* Desktop Sidebar */}
              <div className="hidden md:flex md:w-20 md:flex-col">
                <Sidebar className="border-r" />
              </div>
              {/* Main Content */}
              <div className="flex-1 overflow-auto">
                {isFetching ? <Loader /> : <Outlet />}
              </div>
            </div>
          </div>
        )}
        <Toaster richColors />
      </ThemeProvider>
      <TanStackRouterDevtools position="bottom-left" />
      <ReactQueryDevtools buttonPosition="bottom-right" position="bottom" />
    </>
  );
}
